import React, { FormEvent, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { submitSubscribeRequest } from '../api';
import Seo from '../components/Seo';

const getCountryName = (country: CountryCode) => {
  const displayNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new (Intl as any).DisplayNames(['en'], { type: 'region' })
    : null;

  return displayNames?.of(country) || country;
};

const phoneCountries = getCountries()
  .map((country) => ({
    country,
    callingCode: getCountryCallingCode(country),
    label: `${getCountryName(country)} (+${getCountryCallingCode(country)})`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://trustedoptions.net/#organization',
      name: 'Trusted Options',
      legalName: 'TrafficRush',
      url: 'https://trustedoptions.net/',
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://trustedoptions.net/#logo',
        url: 'https://trustedoptions.net/logo.png',
        contentUrl: 'https://trustedoptions.net/logo.png',
      },
      email: 'support@trustedoptions.net',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Level 3 (Suite No. 3201), Tower Business Center, Triq It-Torri',
        addressLocality: 'Swatar, Birkirkara',
        postalCode: 'BKR 4013',
        addressCountry: 'MT',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@trustedoptions.net',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://trustedoptions.net/#website',
      url: 'https://trustedoptions.net/',
      name: 'Trusted Options',
      publisher: {
        '@id': 'https://trustedoptions.net/#organization',
      },
    },
    {
      '@type': 'Service',
      '@id': 'https://trustedoptions.net/#claim-service',
      name: 'Trusted Options Compensation Claim Service',
      serviceType: 'Compensation claim assessment service',
      description:
        'An online service providing a secure assessment to help users determine whether they may be eligible for a potential compensation claim and, where appropriate, connect with selected claims specialists.',
      url: 'https://trustedoptions.net/',
      provider: {
        '@id': 'https://trustedoptions.net/#organization',
      },
    },
    {
      '@type': 'WebPage',
      '@id': 'https://trustedoptions.net/#webpage',
      url: 'https://trustedoptions.net/',
      name: 'Trusted Options',
      description:
        'Trusted Options helps users explore potential compensation claims and connect with carefully selected claims specialists when appropriate.',
      isPartOf: {
        '@id': 'https://trustedoptions.net/#website',
      },
      about: {
        '@id': 'https://trustedoptions.net/#claim-service',
      },
      publisher: {
        '@id': 'https://trustedoptions.net/#organization',
      },
    },
  ],
};

const Home = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>('GB');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const location = useLocation();

  useEffect(() => {
    try {
      if (location.hash === '#claim-form') {
        const el = document.getElementById('claim-form');
        if (el) {
          setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
        }
      }
    } catch (err) {
      // ignore
    }
  }, [location.hash]);

  const validateEmail = (value: string) => {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    setEmailError(isValid ? '' : 'Please enter a valid email address.');
    return isValid;
  };

  const validatePhone = (value: string, country: CountryCode) => {
    const phoneNumber = parsePhoneNumberFromString(value, country);
    const isValid = Boolean(phoneNumber?.isValid());
    setPhoneError(isValid ? '' : 'Please enter a valid telephone number for the selected country.');
    return phoneNumber?.isValid() ? phoneNumber : null;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    const phoneNumber = validatePhone(phone, phoneCountry);

    if (!phoneNumber) {
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);
    setSubmitStatus('submitting');
    setSubmitMessage('');

    try {
      await submitSubscribeRequest({
        email: email.trim(),
        message: String(formData.get('message') || '').trim(),
        options: {
          first_name: String(formData.get('first_name') || '').trim(),
          last_name: String(formData.get('last_name') || '').trim(),
          country_code: `+${getCountryCallingCode(phoneCountry)}`,
          phone: phoneNumber.formatNational(),
          postcode: String(formData.get('postcode') || '').trim(),
          primary_claim_type: String(formData.get('claim_type') || '').trim(),
          additional_context: String(formData.get('message') || '').trim(),
          accuracy_confirmation: 'Yes',
          data_sharing_consent: 'Yes',
          contact_consent: 'Yes',
          privacy_terms_agreement: 'Yes',
          submitted_at: new Date().toISOString(),
          source_page: 'Home claim assessment',
          page_url: window.location.href,
        },
      });

      form.reset();
      setEmail('');
      setEmailError('');
      setPhone('');
      setPhoneCountry('GB');
      setPhoneError('');
      setSubmitStatus('success');
      setSubmitMessage('Your assessment has been submitted successfully.');
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(error instanceof Error ? error.message : 'Unable to submit your request. Please try again.');
    }
  };

  return (
    <div className="font-sans w-full text-slate-800">
      <Seo
        title="Check Your Compensation Claim Eligibility | Trusted Options"
        description="Complete Trusted Options' free, secure claim eligibility assessment to see if you may qualify for compensation. Fast, confidential, and with no obligation to proceed."
        canonicalUrl="https://trustedoptions.net/"
        schema={homeSchema}
      />

      {/* SECTION 1: HERO & FORM (MIDNIGHT SLATE) */}
      <section
        className="relative text-white py-16 lg:py-24 lg:pt-40 pt-28 border-b-4 border-red-700 mt-[-75px]"
        style={{
          backgroundImage: `url('/hero-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-slate-950/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left Column: Hero Content */}
            <div className="space-y-8 lg:sticky lg:top-36">
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold leading-tight tracking-tight">
                  Check If You May Be Eligible To Make A Claim
                </h1>
              </div>

              <p className="text-lg text-slate-300 leading-relaxed">
                Navigating a compensation claim can be complex. Complete our secure, short assessment below to determine whether you may qualify for a potential claim. If appropriate, your information will be shared with carefully selected claims specialists, legal service providers, or Claims Management Companies who can guide you through the next steps.
              </p>

              {/* BRAND NEW "WHY COMPLETE THIS ASSESSMENT" SECTION */}
              <div className="mt-8">
                <h2 className="text-lg font-bold text-slate-200 mb-5 uppercase tracking-wider flex items-center gap-2">
                  Why Complete This Assessment?
                </h2>

                <div className="space-y-3">
                  {/* Point 1 */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 hover:bg-slate-800/50 transition-all duration-200">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">100% Free Eligibility Check</h3>
                      <p className="text-sm text-slate-400 mt-1">There are no hidden fees or upfront costs to see if you qualify.</p>
                    </div>
                  </div>

                  {/* Point 2 */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 hover:bg-slate-800/50 transition-all duration-200">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">No Obligation to Proceed</h3>
                      <p className="text-sm text-slate-400 mt-1">You are in full control. Review your options with zero pressure.</p>
                    </div>
                  </div>

                  {/* Point 3 */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 hover:bg-slate-800/50 transition-all duration-200">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">Fast & Secure Process</h3>
                      <p className="text-sm text-slate-400 mt-1">Takes less than 60 seconds, protected by bank-level encryption.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: The Form */}
            <div className="bg-white text-slate-900 rounded-2xl shadow-2xl overflow-hidden">

              {/* Top accent bar */}
              <div className="h-1 bg-red-700"></div>

              {/* Form Header */}
              <div className="px-6 md:px-8 pt-6 pb-5 border-b border-slate-100">
                <h2 className="text-2xl font-extrabold text-slate-900">Claim Assessment Form</h2>
                <p className="text-slate-500 text-sm mt-1">Takes less than 2 minutes · 100% confidential</p>
              </div>

              <form id="claim-form" className="px-6 md:px-8 py-6 space-y-8" onSubmit={handleSubmit}>

                {/* ── Step 1: Your Details ── */}
                <fieldset className="space-y-5">
                  <legend className="flex items-center gap-2.5 mb-1">
                    <span className="w-10 h-10 rounded-full bg-red-700 text-white text-lg font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">Your Details</span>
                  </legend>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name <span className="text-red-600">*</span></label>
                      <input name="first_name" type="text" placeholder="John" className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-base focus:border-red-600 focus:ring-0 outline-none transition-colors placeholder:text-slate-400" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name <span className="text-red-600">*</span></label>
                      <input name="last_name" type="text" placeholder="Smith" className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-base focus:border-red-600 focus:ring-0 outline-none transition-colors placeholder:text-slate-400" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address <span className="text-red-600">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={e => {
                          setEmail(e.target.value);
                          if (emailError) validateEmail(e.target.value);
                        }}
                        onBlur={e => validateEmail(e.target.value)}
                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                        title="Please enter a valid email address."
                        placeholder="john@email.com"
                        className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-base focus:border-red-600 focus:ring-0 outline-none transition-colors placeholder:text-slate-400"
                        required
                      />
                      {emailError && <p className="mt-1.5 text-xs font-medium text-red-600">{emailError}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Telephone <span className="text-red-600">*</span></label>
                      <div className="grid grid-cols-[4.25rem_minmax(0,1fr)] gap-2">
                        <select
                          name="phone_country"
                          value={phoneCountry}
                          onChange={e => {
                            const nextCountry = e.target.value as CountryCode;
                            setPhoneCountry(nextCountry);
                            if (phoneError) validatePhone(phone, nextCountry);
                          }}
                          className="w-full border-2 border-slate-200 rounded-lg px-1.5 py-3 text-sm bg-white focus:border-red-600 focus:ring-0 outline-none transition-colors cursor-pointer"
                          aria-label="Telephone country code"
                          required
                        >
                          {phoneCountries.map(({ country, callingCode }) => (
                            <option key={country} value={country}>
                              +{callingCode}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          name="phone"
                          value={phone}
                          inputMode="tel"
                          placeholder="Phone number"
                          onChange={e => {
                            setPhone(e.target.value);
                            if (phoneError) validatePhone(e.target.value, phoneCountry);
                          }}
                          onBlur={e => validatePhone(e.target.value, phoneCountry)}
                          className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-base focus:border-red-600 focus:ring-0 outline-none transition-colors placeholder:text-slate-400"
                          required
                        />
                      </div>
                      {phoneError && <p className="mt-1.5 text-xs font-medium text-red-600">{phoneError}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Postcode <span className="text-red-600">*</span></label>
                    <input name="postcode" type="text" placeholder="SW1A 1AA" className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-base focus:border-red-600 focus:ring-0 outline-none transition-colors placeholder:text-slate-400 uppercase" required />
                  </div>
                </fieldset>

                <hr className="border-slate-100" />

                {/* ── Step 2: Claim Information ── */}
                <fieldset className="space-y-5">
                  <legend className="flex items-center gap-2.5 mb-1">
                    <span className="w-10 h-10 rounded-full bg-red-700 text-white text-lg font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">Claim Information</span>
                  </legend>

                  <div>
                    <label htmlFor="claim_type" className="block text-sm font-medium text-slate-700 mb-1.5">Primary Claim Type <span className="text-red-600">*</span></label>
                    <div className="relative">
                      <select id="claim_type" name="claim_type" className="w-full appearance-none border-2 border-slate-200 rounded-lg px-4 py-3 pr-10 text-base bg-white focus:border-red-600 focus:ring-0 outline-none transition-colors cursor-pointer" required>
                        <option value="">-- Please select an option --</option>
                        <option value="type1">Personal Injury</option>
                        <option value="type2">Financial Mis-selling</option>
                        <option value="type3">Housing Disrepair</option>
                        <option value="other">Other / Not Sure</option>
                      </select>
                      <span className="pointer-events-none absolute inset-y-0 right-[5px] flex items-center pr-2">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Additional Context <span className="text-slate-400 font-normal">(Optional)</span></label>
                    <textarea name="message" rows={3} placeholder="Briefly describe your situation..." className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-base focus:border-red-600 focus:ring-0 outline-none transition-colors placeholder:text-slate-400 resize-none"></textarea>
                  </div>
                </fieldset>

                <hr className="border-slate-100" />

                {/* ── Step 3: Consent & Declarations ── */}
                <fieldset className="space-y-4">
                  <legend className="flex items-center gap-2.5 mb-1">
                    <span className="w-10 h-10 rounded-full bg-red-700 text-white text-lg font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">Consent & Declarations</span>
                  </legend>

                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input name="accuracy_confirmation" type="checkbox" className="mt-1 w-4 h-4 text-red-600 border-2 border-slate-300 rounded focus:ring-red-600 cursor-pointer" required />
                      <span className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">I confirm that the information I have provided is accurate and complete to the best of my knowledge.</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input name="data_sharing_consent" type="checkbox" className="mt-1 w-4 h-4 text-red-600 border-2 border-slate-300 rounded focus:ring-red-600 cursor-pointer" required />
                      <span className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">I agree that my information may be shared with Claims Management Companies, solicitors, legal service providers, and selected partners for the purpose of assessing, managing, and progressing my enquiry.</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input name="contact_consent" type="checkbox" className="mt-1 w-4 h-4 text-red-600 border-2 border-slate-300 rounded focus:ring-red-600 cursor-pointer" required />
                      <span className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">I consent to being contacted by telephone, email, SMS, and other electronic communications regarding my enquiry and related services.</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input name="privacy_terms_agreement" type="checkbox" className="mt-1 w-4 h-4 text-red-600 border-2 border-slate-300 rounded focus:ring-red-600 cursor-pointer" required />
                      <span className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">I have read and agree to the <a href="/privacy-policy/" className="text-red-600 font-semibold hover:underline">Privacy Policy</a> and <a href="/terms/" className="text-red-600 font-semibold hover:underline">Terms & Conditions</a>.</span>
                    </label>
                  </div>
                </fieldset>

                {/* Submit */}
                {submitMessage && (
                  <p className={`text-sm font-semibold ${submitStatus === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                    {submitMessage}
                  </p>
                )}

                <button type="submit" disabled={submitStatus === 'submitting'} className="w-full bg-red-700 hover:bg-red-800 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-colors duration-200 text-base">
                  {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Secure Assessment →'}
                </button>

                <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                  SSL Encrypted · GDPR Compliant
                </p>
              </form>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 2: TYPES OF CLAIMS (LIGHT) */}
      <section className="bg-slate-50 py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Areas of Expertise</h2>
            <p className="text-lg text-slate-600">Our network of specialist partners covers a wide array of claim types. If you've been wronged or injured, you may be entitled to compensation.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            {/* Personal Injury */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md border-red-200 transition-all duration-200 group">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 border border-red-100 bg-red-600 text-white transition-colors duration-300">
                <svg className="w-8 h-8 text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  {/* Bandage / Plaster icon representing injury */}
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14.25 4.5l5.25 5.25c.806.806.806 2.114 0 2.92l-9 9c-.806.806-2.114.806-2.92 0l-5.25-5.25c-.806-.806-.806-2.114 0-2.92l9-9c.806-.806 2.114-.806 2.92 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.75 9.75l4.5 4.5" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 12l.01 .01M10.5 13.5l.01 .01M13.5 10.5l.01 .01" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Personal Injury</h3>
              <p className="text-slate-600 text-sm leading-relaxed">Whether you were involved in a road traffic accident, suffered an injury at work, or experienced a slip and fall, you shouldn't have to bear the financial burden of someone else's negligence.</p>
            </div>

            {/* Financial Mis-selling */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md border-red-200 transition-all duration-200 group">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 border border-red-100 bg-red-600 text-white transition-colors duration-300">
                <svg className="w-8 h-8 text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  {/* Formal Document with Warning/Attention icon */}
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11.25h-6m6 3h-6" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Financial Mis-selling</h3>
              <p className="text-slate-600 text-sm leading-relaxed">If you were sold a financial product (like a pension, mortgage, or investment) that wasn't suitable for your circumstances, you might be eligible to claim back your losses.</p>
            </div>

            {/* Housing Disrepair */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md border-red-200 transition-all duration-200 group">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 border border-red-100 bg-red-600 text-white transition-colors duration-300">
                <svg className="w-8 h-8 text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  {/* House silhouette with a central fracture/crack */}
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 11.25L12 3l9.75 8.25" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.5 10.5v10.5a.75.75 0 00.75.75h13.5a.75.75 0 00.75-.75V10.5" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 3v5l-2.5 3.5 3 4V21.75" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Housing Disrepair</h3>
              <p className="text-slate-600 text-sm leading-relaxed">Tenants suffering from damp, mold, structural damage, or broken heating due to a landlord's failure to maintain the property can claim for repairs and compensation.</p>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 3: HOW IT WORKS (SOLID RED + GLOW ANIMATION) */}
      <section className="bg-gradient-to-b from-white to-slate-50 py-24 relative overflow-hidden">
        {/* Decorative background blurs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-red-100/40 rounded-full filter blur-3xl"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-slate-200/50 rounded-full filter blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
         <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">A Transparent, Simple Process</h2>
            <p className="text-lg text-slate-600 leading-relaxed">We take the confusion out of the claims process. Here is exactly what happens after you click submit.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative pt-8">
            {/* Connecting Faded Line (Desktop Only) */}
            <div className="hidden md:block absolute top-[4.5rem] left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-transparent via-red-200 to-transparent z-0"></div>

            {/* Step 1 */}
            <div className="relative z-10 group">
              <div className="relative h-full bg-white rounded-2xl p-8 pt-14 text-center border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-300">

                {/* Number Badge with Levitate & Glow Animation */}
                <div className="w-20 h-20 bg-red-600 text-white rounded-full flex items-center justify-center text-3xl font-extrabold absolute -top-10 left-1/2 transform -translate-x-1/2 border-4 border-white shadow-md group-hover:-translate-y-2 group-hover:scale-105 group-hover:shadow-[0_0_25px_rgba(220,38,38,0.4)] transition-all duration-300">
                  1
                </div>

                <h3 className="text-xl font-bold mb-4 text-slate-900">Submit Details</h3>
                <p className="text-slate-600 text-sm leading-relaxed">Complete the secure assessment form above. It takes less than a minute and requires only basic information about your situation to get started.</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative z-10 group">
              <div className="relative h-full bg-white rounded-2xl p-8 pt-14 text-center border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-300">

                {/* Number Badge with Levitate & Glow Animation */}
                <div className="w-20 h-20 bg-red-600 text-white rounded-full flex items-center justify-center text-3xl font-extrabold absolute -top-10 left-1/2 transform -translate-x-1/2 border-4 border-white shadow-md group-hover:-translate-y-2 group-hover:scale-105 group-hover:shadow-[0_0_25px_rgba(220,38,38,0.4)] transition-all duration-300 delay-75">
                  2
                </div>

                <h3 className="text-xl font-bold mb-4 text-slate-900">Expert Review</h3>
                <p className="text-slate-600 text-sm leading-relaxed">Our triage team carefully reviews the information provided to determine whether your enquiry meets the initial legal and regulatory criteria for a claim.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative z-10 group">
              <div className="relative h-full bg-white rounded-2xl p-8 pt-14 text-center border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-300">

                {/* Number Badge with Levitate & Glow Animation */}
                <div className="w-20 h-20 bg-red-600 text-white rounded-full flex items-center justify-center text-3xl font-extrabold absolute -top-10 left-1/2 transform -translate-x-1/2 border-4 border-white shadow-md group-hover:-translate-y-2 group-hover:scale-105 group-hover:shadow-[0_0_25px_rgba(220,38,38,0.4)] transition-all duration-300 delay-150">
                  3
                </div>

                <h3 className="text-xl font-bold mb-4 text-slate-900">Specialist Match</h3>
                <p className="text-slate-600 text-sm leading-relaxed">If appropriate, your details are securely passed to a dedicated specialist claims partner or solicitor who will contact you directly to discuss moving forward.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 4: IMPORTANT INFORMATION (MIDNIGHT SLATE) */}
      <section className="bg-slate-950 text-slate-400 py-16 border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="text-slate-600 mb-2">
            <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path></svg>
          </div>
          <h2 className="text-xl font-bold text-slate-200">Important Disclaimer</h2>
          <div className="space-y-4 max-w-4xl mx-auto text-sm leading-relaxed">
            <p>
              Submitting this assessment does not guarantee eligibility for compensation, acceptance of a claim, or any financial outcome. Eligibility is subject to a thorough review by the relevant claims specialist, legal representative, or Claims Management Company. Any potential compensation amounts discussed later in the process are estimates and depend heavily on individual circumstances.
            </p>
            <p>
              By submitting your information, you acknowledge that your personal data may be processed securely in accordance with our <a href="/privacy-policy/" className="text-red-500 hover:text-red-400 underline transition-colors">Privacy Policy</a> and strictly adhered to under applicable data protection laws (such as GDPR).
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
